import { apiClient } from '../client'
import type { Chat, ChatMessage, ItemsResponse } from '../../app/modules/types/chatBot'

/**
 * Centrifugo live-subscription token for a chat's stream channel.
 *
 * Named distinctly from `events.api.ts`'s `CentrifugoTokenResponse` (same
 * pattern, different shape — this one carries `channel`) since both modules
 * are re-exported through the same `services` barrel and a duplicate name
 * would be an ambiguous export.
 *
 * Shape is unchanged from Django; only `ttl_seconds` moved, 600 -> 60.
 */
export interface ChatCentrifugoTokenResponse {
  /** HS256 JWT signed with the shared Centrifugo HMAC secret. */
  token: string
  /** Public WSS URL (through APISIX) the FE should open. */
  url: string
  /**
   * Token TTL in seconds. This is 60 on the new service, down from Django's
   * 600, so the "refresh ~30s before expiry" rule the SPA inherited is now a
   * 50% duty cycle — a refresh every ~30s instead of every ~9.5 minutes. §6 of
   * the contract doc flags exactly this: whoever owns the refresh timer needs
   * to revisit the lead time and confirm nothing expensive runs per refresh.
   */
  ttl_seconds: number
  /** Centrifugo channel name the token is scoped to. */
  channel: string
}

/**
 * One gated call as it arrives on a live `approval_required` event.
 *
 * The same object the persisted block calls an `ApprovalRequestCall`, under
 * different key names: `id`/`name` here are `tool_call_id`/`tool_name` there.
 * `args` is typed to match its persisted twin so normalising one into the other
 * needs no cast.
 */
export interface StreamApprovalCall {
  id: string
  name: string
  args: Record<string, unknown>
}

/**
 * A single event in a chat turn's stream.
 *
 * `seq` is monotonic for the life of the CHAT, not the turn — it comes from a
 * per-channel counter and never restarts. It doubles as the `since` cursor for
 * `getEvents`. Group events by `turn_id`; never assume small per-turn numbers.
 */
export interface ChatStreamEvent {
  /** Protocol version. */
  v: number
  /** Turn this event belongs to; identifies the in-progress assistant turn. */
  turn_id: string
  /** Chat-wide monotonic sequence; also the `since` cursor. */
  seq: number
  type: 'started' | 'thinking' | 'tool_call' | 'tool_result' | 'answer' | 'done' | 'error' | 'approval_required'
  delta?: string
  id?: string
  name?: string
  /** An OBJECT on the new service, not a JSON string. Never `JSON.parse` it. */
  args?: unknown
  status?: string
  summary?: string
  /** On `done`: the full answer text. */
  content?: string
  /**
   * On `error`: the failure reason. The service sends `detail`, NOT `message` —
   * the vendored migration contract said `message` and was wrong, and because
   * the read site defaults with `?? 'Unknown streaming error'` the mistake threw
   * away every reason without ever failing loudly.
   */
  detail?: string
  /** On `approval_required`: every gated call halting this turn. */
  calls?: StreamApprovalCall[]
}

export interface GetEventsResponse {
  events: ChatStreamEvent[]
  next_since: number
}

interface GetEventsParams {
  since: number
}

/** limit/offset for every collection. No page numbers exist — there is no total. */
export interface PageParams {
  limit?: number
  offset?: number
}

export interface CreateChatParams {
  subject_type: string
  subject_id: string
}

export interface ApprovalDecision {
  tool_call_id: string
  approved: boolean
  /** Optional, max 500 chars. Shown to the model as the tool result. */
  denial_message?: string
}

export interface ApprovalsResponse {
  /**
   * `awaiting_more_approvals` is NOT an error: the batch was partly decided,
   * nothing executed, and the card stays open pending the rest.
   *
   * Branch on `status === 'resuming'` and treat everything else as "not
   * resumed" rather than writing an exhaustive switch, so a status added later
   * degrades to the safe arm instead of falling through unhandled.
   */
  status: 'resuming' | 'awaiting_more_approvals'
}

export const chatApi = {
  /**
   * The org's chats, newest first. The default limit is 20 — note the
   * asymmetry with `getMessages` below, whose default is 200, so omitting
   * params truncates here in a way it does not there.
   */
  getList: async (params: PageParams = {}): Promise<Chat[]> => {
    const { data } = await apiClient.get<ItemsResponse<Chat>>('/chat', { params })
    return data.items
  },

  /**
   * The chats anchored to one subject, newest first. This is the finding->chat
   * lookup that replaces the dead `vulnerability.chat_id` field.
   *
   * Both subject params are required together — sending one without the other
   * is a 400, deliberately, so a half-specified filter can never fall back to
   * returning the whole organisation's chats. An unknown `subject_id` yields
   * `{items: []}` with 200, not a 404: "no chats" and "no such finding" are
   * indistinguishable here, which is fine because the page already fetched the
   * finding from Django to render.
   */
  listBySubject: async (
    subjectType: string,
    subjectId: string,
    params: PageParams = {},
  ): Promise<Chat[]> => {
    const { data } = await apiClient.get<ItemsResponse<Chat>>('/chat', {
      params: { subject_type: subjectType, subject_id: subjectId, ...params },
    })
    return data.items
  },

  /** One chat. Carries `user_id`, which the approval owner check needs. */
  getChat: async (chatId: string): Promise<Chat> => {
    const { data } = await apiClient.get<Chat>(`/chat/${chatId}`)
    return data
  },

  /**
   * Message history, oldest first. `offset` therefore counts from the OLDEST
   * message — the opposite direction from the chat list. Default limit is 200,
   * so omitting params behaves as it always did for any real conversation.
   */
  getMessages: async (chatId: string, params: PageParams = {}): Promise<ChatMessage[]> => {
    const { data } = await apiClient.get<ItemsResponse<ChatMessage>>(
      `/chat/${chatId}/messages`,
      { params },
    )
    return data.items
  },

  /**
   * Create a chat, free or anchored. Both subject fields must be present or
   * both absent — 400 otherwise. The subject IS validated here (unlike the
   * filter), so an unknown id or one outside the org fails the create: surface
   * that error rather than opening an empty chat.
   */
  create: async (params?: CreateChatParams): Promise<Chat> => {
    const { data } = await apiClient.post<Chat>('/chat', params ?? {})
    return data
  },

  /**
   * Send a user message. Returns 202 with the PERSISTED user message — real id
   * and timestamp, immediately. The assistant turn arrives over the stream.
   */
  sendMessage: async (chatId: string, content: string): Promise<ChatMessage> => {
    const { data } = await apiClient.post<ChatMessage>(`/chat/${chatId}/messages`, { content })
    return data
  },

  /**
   * Decide gated tool calls. One batch, one request — every `tool_call_id` must
   * belong to the same assistant message. The whole batch is authorised before
   * any decision is recorded, so a rejection leaves nothing partly applied.
   *
   * Two different 403s, with two different messages to the user (§14):
   * - caller does not own the chat -> "waiting on the chat owner"
   * - caller lacks `api:patcher`/`edit` or `api:chatbot`/`add` for that tool ->
   *   "you do not have permission to approve this action"
   *
   * 409 means the call was already decided — "already handled", not an error;
   * refetch and re-render. 404 means an unknown `tool_call_id`, i.e. a stale
   * card: refetch the messages rather than reporting a failure.
   *
   * The interceptor in `SetupAxios.tsx` is set up for this: `/approvals` is on
   * its toast-exemption list, so a 409 or 404 reaches your `.catch()` without
   * the user seeing anything, AND on its forbidden-redirect exemption list, so
   * a 403 no longer dispatches `auth:forbidden` and replaces the route with
   * `/org-dashboard`. Both were necessary — a toast the caller cannot suppress
   * (it fires before any `.catch()`) and a redirect that unmounts the very card
   * meant to render the message. That means the errors above are now yours to
   * render, and an unhandled rejection here is completely silent.
   */
  decideApprovals: async (
    chatId: string,
    decisions: ApprovalDecision[],
  ): Promise<ApprovalsResponse> => {
    const { data } = await apiClient.post<ApprovalsResponse>(
      `/chat/${chatId}/approvals`,
      { decisions },
    )
    return data
  },

  /** Returns 201 with the updated chat, so `is_helpful` can render from it. */
  sendFeedback: async (chatId: string, isHelpful: boolean): Promise<Chat> => {
    const { data } = await apiClient.post<Chat>(`/chat/${chatId}/feedback`, { is_helpful: isHelpful })
    return data
  },

  /**
   * Mint a short-lived Centrifugo subscription token scoped to this chat's
   * stream channel. TTL is 60s on the new service (was 600s).
   */
  centrifugoToken: async (chatId: string): Promise<ChatCentrifugoTokenResponse> => {
    const { data } = await apiClient.get<ChatCentrifugoTokenResponse>(
      `/chat/${chatId}/stream-token`,
    )
    return data
  },

  /**
   * Backfill/catch-up read of chat stream events since a given cursor.
   * Retains the last 500 events per chat with no TTL, so a next-morning reopen
   * still replays correctly.
   */
  getEvents: async (chatId: string, params: GetEventsParams): Promise<GetEventsResponse> => {
    const { data } = await apiClient.get<GetEventsResponse>(
      `/chat/${chatId}/events`,
      { params },
    )
    return data
  },
}
